import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { Button, Popover, Space, Table, TableColumnsType } from "antd";
import { MovementsByEquipmentDetail } from "api/generated";
import { Link, RouteComponentProps } from "react-router-dom";
import { UsePaginationTableReturn } from "hooks/commons/usePaginationTable";
import { useContext, useMemo } from "react";
import { UseTableSelectionReturn } from "hooks/commons/useTableSelection";
import { COMPLEMENT_PARENT } from "routes/cfdiComplements/ComplementsScreen";
import DistributionPointSelected from "context/MyDistributionPoint";
import dayjs from "dayjs";

interface HoseBySaleTableProps extends RouteComponentProps {
  selection: UseTableSelectionReturn;
  isLoading: boolean;
  reloadTable: () => void;
  pagination: UsePaginationTableReturn<MovementsByEquipmentDetail>;
  deleteComplement: (record: MovementsByEquipmentDetail) => void;
}

function HoseBySaleTable({
  selection,
  isLoading,
  pagination,
  deleteComplement,
  location,
}: HoseBySaleTableProps) {
  const ventas = useMemo(() => pagination.items, [pagination.items]);
  const { distributionPoint } = useContext(DistributionPointSelected);

  const columns: TableColumnsType<MovementsByEquipmentDetail> = [
    {
      title: "Fecha y hora",
      dataIndex: "movementDate",
      width: "8rem",
      align: "center",
      render: (movementDate) =>
        dayjs(movementDate).format("YYYY/MM/DD HH:mm:ss"),
    },
    { title: "Folio", dataIndex: "movementFolio", width: "5rem" },
    {
      title: "Tipo",
      dataIndex: "movementType",
      width: "8rem",
      align: "center",
    },
    {
      title: "Dispensario",
      dataIndex: "responseDispensaryCode",
      width: "7rem",
      align: "center",
    },
    {
      title: "Manguera",
      dataIndex: "responseEquipmentKey",
      width: "7rem",
      align: "center",
    },
    {
      title: "Tipo de producto",
      dataIndex: "responseProductDesc",
      width: "6rem",
      align: "center",
    },
    {
      title: "Litros vendidos",
      dataIndex: "volume",
      width: "6rem",
      align: "center",
    },
    {
      title: "Precio por litro",
      dataIndex: "price",
      width: "6rem",
      align: "center",
      render: (text) => `$${text}`,
    },
    {
      title: "Importe total",
      dataIndex: "total",
      width: "8rem",
      align: "center",
      render: (text) => `$${text}`,
    },

    {
      title: "Complemento",
      dataIndex: "complement",
      width: "8rem",
      align: "center",
      render: (_, record) => {
        const isEditable = record.isEditAllowed;
        const hasComplements = !!record.complement;

        if (!isEditable && !hasComplements) {
          return <span>Sin complemento asociado</span>;
        }
        const complementState = isEditable
          ? hasComplements
            ? "edit"
            : "add"
          : "view";

        const complementConfig = {
          add: {
            label: "Agregar información para complemento",
            icon: <PlusOutlined />,
          },
          edit: {
            label: "Editar información de complemento",
            icon: <EditOutlined />,
          },
          view: {
            label: "Ver información de complemento",
            icon: <EyeOutlined />,
          },
        };
        const currentComplement = complementConfig[complementState];

        return (
          <Space>
            <Link
              key="toComplement"
              to={{
                pathname: `/complement/${record.idMovement}/${COMPLEMENT_PARENT.VENTA}/${distributionPoint?.id}`,
                state: {
                  from: location.pathname + location.search,
                },
              }}
            >
              <Popover content={currentComplement.label}>
                <Button icon={currentComplement.icon} />
              </Popover>
            </Link>
            {hasComplements && (
              <Popover content="Eliminar información de complemento">
                <Button
                  icon={<DeleteOutlined />}
                  danger
                  onClick={() => {
                    deleteComplement(record);
                  }}
                />
              </Popover>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <Table
      rowSelection={selection.rowSelection}
      rowKey="idMovement"
      style={{ width: "100%" }}
      columns={columns}
      dataSource={ventas}
      loading={isLoading}
      pagination={{
        ...pagination.paginationConfig,
        onChange: pagination.onPageChange,
        onShowSizeChange: pagination.onPageSizeChange,
      }}
      scroll={{
        x: 1500,
        y: "calc(100vh - 600px)",
      }}
    />
  );
}

export default HoseBySaleTable;
